import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import "./NewPeriodSelector.scss";
import { storedLocale } from "../../Authentication/util";
import {
  getLongPeriodFormat,
  getPerformanceYear,
  getPerformanceYearShort,
  getSelectedPerformanceYearData,
  getShortPeriodFormat,
  getYear,
} from "../../../common/util/period";
import { IconCarrot } from "../../../common/icons/IconCarrot/IconCarrot";
import TEALIUM from "../../../common/enums/tealium-enums";
import { useAppDispatch, useAppSelector } from "../../../store";
import { SELECTOR_TYPE, updateNextPeriodFlag, updatePeriod } from "../../../reducers/period";
import defaultContent from "../../../content/periodSelector";
import commonContent from "../../../content/common";
import { track } from "../../../common/util/tealium";
import { IconDropdownCarrot } from "../../../common/icons/IconDropdownCarrot/IconDropdownCarrot";
import { PeriodDrawer, type PeriodGroup } from "./PeriodDrawer";

export const NewPeriodSelector = () => {
  const { selectedPeriod, periods, formattedPeriods, selectorType } = useAppSelector((state) => state.period);
  const dispatch = useAppDispatch();
  const locale = storedLocale();
  const { t } = useTranslation(["periodSelector", "common"]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isPerformanceYear, setIsPerformanceYear] = useState(false);
  const [mobileSelectorData, setMobileSelectorData] = useState<string[]>([]);
  const [leftCarrotColor, setLeftCarrotColor] = useState<"black" | "blue">("black");
  const [rightCarrotColor, setRightCarrotColor] = useState<"black" | "blue">("black");
  const [selectedFromDropdown, setSelectedFromDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const periodListRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pillButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (selectedFromDropdown) {
      handleTealiumTrackClickCategoryPeriodSelector(selectedPeriod);
      setSelectedFromDropdown(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFromDropdown]);

  useEffect(() => {
    setIsPerformanceYear(selectorType === SELECTOR_TYPE.PY);
  }, [selectorType]);

  useEffect(() => {
    if (!isDropdownVisible) return;
    const items = isPerformanceYear ? formattedPeriods.map((p) => p.period) : periods;
    const selectedIndex = items.indexOf(selectedPeriod);
    setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);

    requestAnimationFrame(() => {
      if (!dropdownRef.current || !periodListRef.current) return;
      const index = periods.indexOf(selectedPeriod);
      const item = periodListRef.current.children[index] as HTMLElement;
      if (item) {
        dropdownRef.current.scrollTop = item.offsetTop - dropdownRef.current.clientHeight / 2 + item.clientHeight / 2;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDropdownVisible]);

  const closeDropdown = useCallback(() => {
    setIsDropdownVisible(false);
    setIsMobileDrawerOpen(false);
    pillButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isDropdownVisible) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isDropdownVisible, closeDropdown]);

  useEffect(() => {
    setMobileSelectorData(isPerformanceYear ? formattedPeriods.map((p) => p.period) : periods);
  }, [isPerformanceYear, formattedPeriods, periods]);

  const capitalizeFirstLetter = (period: string) => period.charAt(0).toUpperCase() + period.slice(1);

  const formatPeriodForSelector = (period: string) => {
    const PY = `${t("performanceYearShort", commonContent["performanceYearShort"], {
      ns: "common",
    })}${getPerformanceYearShort(getPerformanceYear(period))}`;
    if (isPerformanceYear) return PY;
    return capitalizeFirstLetter(getShortPeriodFormat(period, locale));
  };

  const formatPeriodNumeric = (period: string) => {
    if (isPerformanceYear) return formatPeriodForSelector(period);
    const year = parseInt(period.slice(0, 4));
    const month = parseInt(period.slice(4, 6)) - 1;
    const date = new Date(year, month);
    const longName = new Intl.DateTimeFormat(locale, { month: "long" }).format(date);
    const shortName = new Intl.DateTimeFormat(locale, { month: "short" }).format(date);
    const monthName = capitalizeFirstLetter(longName.length > 5 ? shortName : longName);
    return capitalizeFirstLetter(getShortPeriodFormat(period, locale));
  };

  const formatPeriodsForSelectorList = (period: string) => {
    const { selectedPYEndPeriod } = getSelectedPerformanceYearData(period);
    return isPerformanceYear
      ? `${t("performanceYearShort", commonContent["performanceYearShort"], { ns: "common" })} ` +
          getYear(selectedPYEndPeriod, locale)
      : capitalizeFirstLetter(getLongPeriodFormat(period, locale));
  };

  const updateSelector = (period: string) => {
    dispatch(updateNextPeriodFlag({ isNextPeriod: true }));
    dispatch(updatePeriod({ period }));

    document.dispatchEvent(
      new CustomEvent("period-changed", {
        bubbles: true,
        composed: true,
        detail: { period },
      }),
    );
    setIsDropdownVisible(false);
    setIsMobileDrawerOpen(false);
  };

  const stepToNewPeriod = (type: "next" | "prev") => {
    const currentIndex = periods.indexOf(selectedPeriod);
    if (type === "next" && periods[currentIndex - 1]) {
      updateSelector(periods[currentIndex - 1]);
    }
    if (type === "prev" && periods[currentIndex + 1]) {
      updateSelector(periods[currentIndex + 1]);
    }
  };

  const handlePillClick = () => {
    setIsDropdownVisible((prev) => !prev);
  };

  const handlePillKeyDown = (event: React.KeyboardEvent) => {
    if (isDropdownVisible) {
      handleDropdownKeyDown(event);
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setIsDropdownVisible(true);
    }
  };

  const handleMobileInlineClick = () => {
    setIsMobileDrawerOpen(true);
  };

  const handleBackdropClick = () => {
    closeDropdown();
  };

  const getDropdownItems = useCallback(() => {
    return isPerformanceYear ? formattedPeriods.map((p) => p.period) : periods;
  }, [isPerformanceYear, formattedPeriods, periods]);

  const handleDropdownKeyDown = (event: React.KeyboardEvent) => {
    const items = getDropdownItems();
    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        setFocusedIndex((prev) => {
          const next = Math.min(prev + 1, items.length - 1);
          scrollItemIntoView(next);
          return next;
        });
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        setFocusedIndex((prev) => {
          const next = Math.max(prev - 1, 0);
          scrollItemIntoView(next);
          return next;
        });
        break;
      }
      case "Home": {
        event.preventDefault();
        setFocusedIndex(0);
        scrollItemIntoView(0);
        break;
      }
      case "End": {
        event.preventDefault();
        setFocusedIndex(items.length - 1);
        scrollItemIntoView(items.length - 1);
        break;
      }
      case "Enter":
      case " ": {
        event.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < items.length) {
          updateSelector(items[focusedIndex]);
          setSelectedFromDropdown(true);
          pillButtonRef.current?.focus();
        }
        break;
      }
      case "Escape": {
        event.preventDefault();
        setIsDropdownVisible(false);
        pillButtonRef.current?.focus();
        break;
      }
      case "Tab": {
        setIsDropdownVisible(false);
        break;
      }
    }
  };

  const scrollItemIntoView = (index: number) => {
    requestAnimationFrame(() => {
      if (!periodListRef.current) return;
      const item = periodListRef.current.children[index] as HTMLElement;
      item?.scrollIntoView({ block: "nearest" });
    });
  };

  const handleTealiumTrackClickCategoryPeriodSelector = (period: string) => {
    track(TEALIUM.U_TAG_METHOD.LINK, TEALIUM.EVENT_NAME.CLICK_CATEGORY, "", "date_selector", "", period);
  };

  const handleLeftChevronClick = () => {
    stepToNewPeriod("prev");
    track(TEALIUM.U_TAG_METHOD.LINK, TEALIUM.EVENT_NAME.CLICK_ACTION, "", "date_selector", "", "previous_period");
  };

  const handleRightChevronClick = () => {
    stepToNewPeriod("next");
    track(TEALIUM.U_TAG_METHOD.LINK, TEALIUM.EVENT_NAME.CLICK_ACTION, "", "date_selector", "", "next_period");
  };

  const previousDisabled = !periods || periods.length === 0 || selectedPeriod === periods[periods.length - 1];
  const nextDisabled = !periods || periods.length === 0 || selectedPeriod === periods[0];

  const getMobilePeriodGroups = (): PeriodGroup[] => {
    if (isPerformanceYear) {
      return [{ label: "", periods: mobileSelectorData }];
    }
    const groupMap: { label: string; periods: string[] }[] = [];
    mobileSelectorData.forEach((period) => {
      const label = t("performanceYear", defaultContent.performanceYear, {
        performanceYear: getPerformanceYearShort(period),
      }).toString();
      const existing = groupMap.find((g) => g.label === label);
      if (existing) {
        existing.periods.push(period);
      } else {
        groupMap.push({ label, periods: [period] });
      }
    });
    return groupMap;
  };

  const DateList = () => {
    const items = isPerformanceYear ? formattedPeriods.map((p) => p.display) : periods;
    return (
      <div
        className="NewPeriodSelector__list"
        role="listbox"
        id="new-period-selector__list"
        ref={periodListRef}
        aria-label={`${t("bonusPeriodList", defaultContent["bonusPeriodList"])}`}
      >
        {items.map((p, index) => {
          const displayPeriod = isPerformanceYear ? (formattedPeriods[index]?.period ?? p) : p;
          const isFocused = index === focusedIndex;
          return (
            <div
              key={p}
              id={`new-period-selector__list-item-${index}`}
              className={[
                "NewPeriodSelector__listItem",
                selectedPeriod === displayPeriod ? "NewPeriodSelector__listItem--active" : "",
                isFocused ? "NewPeriodSelector__listItem--focused" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              role="option"
              aria-selected={selectedPeriod === displayPeriod}
              data-value={displayPeriod}
              tabIndex={isFocused ? 0 : -1}
              onClick={() => {
                updateSelector(displayPeriod);
                setSelectedFromDropdown(true);
                pillButtonRef.current?.focus();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  updateSelector(displayPeriod);
                  setSelectedFromDropdown(true);
                  pillButtonRef.current?.focus();
                }
              }}
            >
              {formatPeriodsForSelectorList(p)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="NewPeriodSelector" data-testid="new-period-selector">
      {/* Desktop layout */}
      <div className="NewPeriodSelector__desktop">
        <button
          type="button"
          className={`NewPeriodSelector__chevronBtn${previousDisabled ? " NewPeriodSelector__chevronBtn--disabled" : ""}`}
          aria-label={`${t("previousArrowLabel", defaultContent["previousArrowLabel"])}`}
          onClick={handleLeftChevronClick}
          onMouseEnter={() => setLeftCarrotColor("blue")}
          onMouseLeave={() => setLeftCarrotColor("black")}
          disabled={previousDisabled}
          data-testid="period-selector-prev"
        >
          <IconCarrot
            orientation="left"
            color={isDropdownVisible || leftCarrotColor === "blue" ? "blue" : "black"}
            disabled={previousDisabled}
          />
        </button>

        <div className="NewPeriodSelector__pillWrapper">
          <button
            type="button"
            className={`NewPeriodSelector__pill${isDropdownVisible ? " NewPeriodSelector__pill--open" : ""}`}
            onClick={handlePillClick}
            onKeyDown={handlePillKeyDown}
            aria-haspopup="listbox"
            aria-expanded={isDropdownVisible}
            aria-controls={isDropdownVisible ? "new-period-selector__list" : undefined}
            aria-activedescendant={
              isDropdownVisible && focusedIndex >= 0 ? `new-period-selector__list-item-${focusedIndex}` : undefined
            }
            aria-label={`${t("selected", defaultContent["selected"])} ${formatPeriodsForSelectorList(selectedPeriod)}`}
            ref={pillButtonRef}
            data-testid="period-selector-pill"
          >
            <span className="NewPeriodSelector__pillText">{formatPeriodNumeric(selectedPeriod)}</span>
          </button>

          {isDropdownVisible && (
            <div className="NewPeriodSelector__dropdown" ref={dropdownRef} data-testid="period-selector-dropdown">
              <DateList />
            </div>
          )}

          {isDropdownVisible && (
            <div className="NewPeriodSelector__backdrop--invisible" onClick={handleBackdropClick} role="presentation" />
          )}
        </div>

        <button
          type="button"
          className={`NewPeriodSelector__chevronBtn${nextDisabled ? " NewPeriodSelector__chevronBtn--disabled" : ""}`}
          aria-label={`${t("nextArrowLabel", defaultContent["nextArrowLabel"])}`}
          onClick={handleRightChevronClick}
          onMouseEnter={() => setRightCarrotColor("blue")}
          onMouseLeave={() => setRightCarrotColor("black")}
          disabled={nextDisabled}
          data-testid="period-selector-next"
        >
          <IconCarrot
            orientation="right"
            color={isDropdownVisible || rightCarrotColor === "blue" ? "blue" : "black"}
            disabled={nextDisabled}
          />
        </button>
      </div>

      {/* Mobile layout */}
      <div className="NewPeriodSelector__mobile">
        <button
          type="button"
          className="NewPeriodSelector__inlineDropdown"
          onClick={handleMobileInlineClick}
          aria-haspopup="listbox"
          aria-expanded={isMobileDrawerOpen}
          aria-label={`${t("selected", defaultContent["selected"])} ${formatPeriodsForSelectorList(selectedPeriod)}`}
          data-testid="period-selector-mobile"
        >
          <span className="NewPeriodSelector__inlineText">{formatPeriodForSelector(selectedPeriod)}</span>
          <IconDropdownCarrot color="black" open={isMobileDrawerOpen} />
        </button>
      </div>

      {isMobileDrawerOpen &&
        createPortal(
          <PeriodDrawer
            groups={getMobilePeriodGroups()}
            selectedPeriod={selectedPeriod}
            onSelect={updateSelector}
            onClose={handleBackdropClick}
            formatPeriod={formatPeriodsForSelectorList}
          />,
          document.body,
        )}
    </div>
  );
};
